// Types shared by the API and the web app. Wire format only: no server internals.

export type Channel = 'call' | 'sms' | 'email' | 'form';
/** `auto` is a system message (auto-reply text, workflow email). It never counts as a rep reply. */
export type Direction = 'in' | 'out' | 'auto';
export type Outcome = 'booked' | 'follow_up' | 'not_booked' | 'missed';
export type Grade = 'A' | 'B' | 'C' | 'D';
export type Tone = 'green' | 'red' | 'amber' | 'grey' | 'blue';
export type EngagementLabel = 'High' | 'Medium' | 'Low';

export interface Flag {
  label: string;
  tone: Tone;
}

export const OUTCOME_LABEL: Record<Outcome, string> = {
  booked: 'Booked',
  follow_up: 'Follow-up',
  not_booked: 'Not booked',
  missed: 'Missed',
};

export const OUTCOME_TONE: Record<Outcome, Tone> = {
  booked: 'green',
  follow_up: 'amber',
  not_booked: 'grey',
  missed: 'red',
};

export const GRADE_TONE: Record<Grade, Tone> = { A: 'green', B: 'blue', C: 'amber', D: 'red' };

export const CHANNEL_LABEL: Record<Channel, string> = { call: 'Call', sms: 'SMS', email: 'Email', form: 'Form' };
/** Lower-case nouns used in sentences: "1 call, 2 texts". */
export const CHANNEL_NOUN: Record<Channel, string> = { call: 'call', sms: 'text', email: 'email', form: 'form fill' };

export interface RepOption {
  id: string;
  name: string;
}

export interface MeResponse {
  /** Owners see every company and Admin; managers their company; reps only leads assigned to them. */
  user: { name: string; email: string; title: string; initials: string; isOwner: boolean; role: 'owner' | 'manager' | 'rep' };
  /** How people sign in: Google, or only the shared password. */
  signIn: 'google' | 'password';
  company: { id: string; name: string; timeZone: string; crm: 'highlevel'; status: CompanyStatus };
  companies: { id: string; name: string }[];
}

export type CompanyStatus = 'connected' | 'importing' | 'token_invalid' | 'disconnected';

export interface NavCounts {
  awaitingReply: number;
  rescueOpen: number;
  coachingMoments: number;
}

// ---------- Conversations ----------

export interface TouchChip {
  channel: Channel;
  /** 'in'/'out' for calls; 'manual' (lead or rep) or 'auto' for texts and emails; 'all' for forms. */
  kind: 'in' | 'out' | 'manual' | 'auto' | 'all';
  label: string;
  count: number;
}

export interface ConversationRow {
  leadId: string;
  name: string;
  grade: Grade | null;
  rep: RepOption | null;
  /** Chips for the Touches column: calls split in/out, texts and emails split manual/automated. */
  touches: TouchChip[];
  engagement: { score: number; label: EngagementLabel };
  latest: { text: string; at: string; who: string; channel: Channel; flag: Flag | null };
  awaiting: boolean;
  outcome: Outcome;
}

export interface ConversationsResponse {
  rows: ConversationRow[];
  volumes: { days: number; calls: number; texts: number; emails: number };
  reps: RepOption[];
  timeZone: string;
}

// ---------- Lead journey ----------

export interface TimelineItem {
  id: string;
  channel: Channel;
  direction: Direction;
  title: string;
  by: string;
  at: string;
  body: string | null;
  flag: Flag | null;
  isCall: boolean;
  hasRecording: boolean;
  durationSec: number | null;
  /** For a call: what it was and the AI's one-line read, once analyzed. */
  callSummary: { type: CallType; text: string } | null;
}

export interface MilestoneChip {
  id: string;
  name: string;
  optional: boolean;
  state: 'reached' | 'next' | 'future';
}

export interface ChannelStat {
  total: number;
  fromLead: number;
}

export interface KeyMoment {
  t: number;
  text: string;
  flag: string;
  tone: Tone;
}

/** What a call turned out to be. Only conversations are scored. */
export type CallType = 'conversation' | 'voicemail' | 'voice_agent' | 'no_conversation';
export const CALL_TYPE_LABEL: Record<CallType, string> = {
  conversation: 'Conversation',
  voicemail: 'Voicemail',
  voice_agent: 'Voice AI',
  no_conversation: 'No conversation',
};

export interface CallAnalysisDto {
  callType: CallType;
  summary: string;
  wanted: string[];
  objections: string[];
  doneWell: string[];
  missed: string[];
  keyMoments: KeyMoment[];
}

export type GradeCheckResult = 'met' | 'unconfirmed' | 'found' | 'not_found';

export interface LeadJourneyResponse {
  lead: {
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
    source: string | null;
    rep: RepOption | null;
    outcome: Outcome;
    crmUrl: string;
  };
  awaiting: boolean;
  milestones: { items: MilestoneChip[]; reached: number; required: number; heard: AiMilestoneDto[] };
  stats: { calls: ChannelStat; texts: ChannelStat; emails: ChannelStat; forms: ChannelStat };
  engagement: { score: number; label: EngagementLabel; why: string };
  touchSummary: string;
  /** Total touches for the lead. The timeline holds the most recent ones when this is larger. */
  totalTouches: number;
  timeline: TimelineItem[];
  /** The latest answered call, which drives the player, AI summary and call score. */
  call: {
    touchId: string;
    durationSec: number;
    hasRecording: boolean;
    score: number | null;
    analysis: CallAnalysisDto | null;
    /** Why there's no analysis yet, in plain words. Null when analyzed. */
    notAnalyzed: string | null;
    criteria: { name: string; weight: number; pass: boolean }[];
  } | null;
  grade: {
    ai: Grade | null;
    final: Grade | null;
    reason: string;
    override: { from: Grade; to: Grade; by: string; at: string; note: string } | null;
    checks: { text: string; group: 'Must-have' | 'Disqualifier' | 'Preferred'; result: GradeCheckResult }[];
  };
  summary: string;
  nextSteps: string[];
  notes: NoteDto[];
  timeZone: string;
  /** The lead's latest rescue item, if it was ever flagged. */
  rescue: LeadRescueDto | null;
  currency: string;
  leadValue: number | null;
}

export interface NoteDto {
  id: string;
  author: string;
  text: string;
  at: string;
  source: 'crm' | 'coachrex';
}

// ---------- Small pure helpers used on both sides ----------

/** "17 min later", "3 hrs later", "2 days later". */
export function gapLabel(fromIso: string, toIso: string): { label: string; bold: boolean } {
  const ms = Date.parse(toIso) - Date.parse(fromIso);
  const m = Math.max(0, Math.round(ms / 60000));
  if (m < 60) return { label: `${m} min later`, bold: false };
  if (m < 1440) {
    const h = Math.round(m / 60);
    return { label: `${h} hr${h > 1 ? 's' : ''} later`, bold: false };
  }
  const d = Math.round(m / 1440);
  return { label: `${d} day${d > 1 ? 's' : ''} later`, bold: true };
}

/** Short elapsed label used in flags: "38m", "3h", "2d". */
export function elapsedShort(ms: number): string {
  const m = Math.max(1, Math.round(ms / 60000));
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0]!.toUpperCase())
    .join('');
}

export function formatDuration(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ---------- Admin (app owner) ----------

export const INDUSTRIES = ['ADU / construction', 'Legal intake', 'Med spa', 'Real estate', 'Home services', 'SaaS / B2B', 'Other'] as const;
export type Industry = (typeof INDUSTRIES)[number];

export const TIME_ZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Phoenix', label: 'Arizona Time' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/New_York', label: 'Eastern Time' },
] as const;

export const CURRENCIES = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'CAD', label: 'CAD ($)' },
] as const;

/** Scopes the Private Integration needs. forms.readonly is needed for form fills. */
export const HIGHLEVEL_SCOPES = [
  'contacts.readonly',
  'contacts.write',
  'conversations.readonly',
  'conversations/message.readonly',
  'calendars.readonly',
  'calendars/events.readonly',
  'opportunities.readonly',
  'locations.readonly',
  'locations/tags.readonly',
  'users.readonly',
  'forms.readonly',
] as const;

export interface AdminCompanyRow {
  id: string;
  name: string;
  industry: string | null;
  status: CompanyStatus;
  /** 0–100 while the first import runs. */
  importPct: number | null;
  locationMasked: string;
  managers: number;
  leads90d: number;
  lastSyncAt: string | null;
  current: boolean;
}

export interface ConnectionTestRequest {
  locationId: string;
  token: string;
}

export interface ConnectionTestResponse {
  ok: boolean;
  error?: string;
  subAccount?: string;
  timeZone?: string;
  counts?: { contacts: number; conversations: number; calendars: number; tags: number; users: number };
  users?: { crmId: string; name: string; email: string | null; role: 'admin' | 'user' }[];
}

export interface CreateCompanyRequest {
  name: string;
  industry: Industry;
  timeZone: string;
  currency: string;
  locationId: string;
  token: string;
  extraManagers: string[];
  /** HighLevel users to mark inactive (not in sales). */
  excludedUserIds: string[];
  /** Days of history to import (0: from today onward). */
  importDays: ImportDays;
}

/** How far back an import reads from the CRM. 0 = from midnight today (company time zone) onward. */
export type ImportDays = 0 | 14 | 30 | 90 | 180;
export const IMPORT_DAY_OPTIONS: { days: ImportDays; label: string }[] = [
  { days: 0, label: 'Today' },
  { days: 14, label: 'Last 2 weeks' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
  { days: 180, label: 'Last 6 months' },
];

export interface AdminAiStatus {
  /** False when the server has no Anthropic API key. */
  available: boolean;
  enabled: boolean;
  enabledAt: string | null;
  model: string;
  monthSpendUsd: number;
  monthCapUsd: number;
  callsAnalyzed30d: number;
  leadsGraded30d: number;
  transcripts: { ready: number; waiting: number; none: number };
  /** Where call transcripts come from. */
  transcription: 'deepgram' | 'crm';
  backfill: { days: number; leads: number; estimatedUsd: number }[];
  batchesRunning: number;
  /** Most recent failure in the last 24 hours. */
  lastError: { message: string; at: string } | null;
  history: AdminHistory;
  /** Where live analysis can start from, with what each would add (only work not done yet is counted). */
  start: { blocked: string | null; options: { key: AiStart; from: string; calls: number; leads: number; usd: number }[] };
}

export type AiStart = 'today' | 'days14' | 'days30';

/** Importing and analyzing older history (6 months). Its costs are approved up front and kept out of the monthly cap. */
export interface AdminHistory {
  /** How far back history reaches (or, before the first import, would reach). */
  days: number;
  /** How far back the owner can choose to go from here, with a rough cost for each. */
  options: { days: number; usd: number }[];
  stage: 'importing' | 'transcribing' | 'analyzing' | 'grading' | 'done' | null;
  from: string;
  /** Before it starts, scaled from what is imported so far. */
  estimate: { calls: number; minutes: number; leads: number; usd: number };
  /** Percent of the older conversations imported, while importing. */
  importProgress: number | null;
  transcripts: { ready: number; waiting: number };
  spentUsd: number;
}

// ---------- Scoring setup (Rep scoring, Lead scoring) ----------

export type AppliesTo = 'all' | 'inbound' | 'outbound';

export interface RepCriterionDto {
  id: string;
  name: string;
  passDefinition: string;
  weight: number;
  enabled: boolean;
  appliesTo: AppliesTo;
  /** Share of analyzed calls in the last 90 days that passed; null before any call was scored on it. */
  passRate: number | null;
  scoredCalls: number;
}

export interface RepScoringResponse {
  criteria: RepCriterionDto[];
  analyzedCalls90d: number;
}

export interface LeadRulesDto {
  template: string;
  prompt: string;
  mustHaves: string[];
  disqualifiers: string[];
  preferred: string[];
  objections: string[];
  gradeMeanings: Record<Grade, string>;
}

export interface LeadScoringResponse {
  rules: LeadRulesDto & { version: number; updatedAt: string };
  templates: Record<string, { prompt: string; mustHaves: string[]; disqualifiers: string[]; preferred: string[] }>;
}

// ---------- Phase 3: Rescue, Overview, grade changes ----------

export type RescueStatus = 'open' | 'completed' | 'recovered' | 'lost' | 'not_needed';

export const RESCUE_STATUS_LABEL: Record<RescueStatus, string> = {
  open: 'Open',
  completed: 'Completed',
  recovered: 'Recovered',
  lost: 'Lost',
  not_needed: 'Not needed',
};

export interface RescueRowDto {
  id: string;
  leadId: string;
  leadName: string;
  grade: Grade | null;
  rep: RepOption | null;
  reason: string;
  channel: Channel;
  status: RescueStatus;
  /** Second line under the reason: last update, credit, "Set by", "back to Open in 48h". */
  statusLine: string;
  value: number | null;
  priority: 'high' | 'follow_up';
  flaggedAt: string;
  /** When the lead started waiting: the triggering touch, or the flag time for manual adds. */
  since: string;
  statusAt: string;
  crmUrl: string;
}

/** Every active item plus the last 30 days of closed ones. Tabs, rep filter and sort are applied in the client. */
export interface RescueListResponse {
  rows: RescueRowDto[];
  /** Speed to lead over the last 30 days: first inbound touch to first rep reply, any channel. */
  avgFirstReplyMin: number | null;
  currency: string;
  reps: RepOption[];
  backToOpenHours: number;
}

export interface RescueEventDto {
  id: string;
  status: RescueStatus;
  note: string;
  by: string;
  manual: boolean;
  credit: string | null;
  at: string;
}

export interface LeadRescueDto {
  id: string;
  status: RescueStatus;
  reason: string;
  history: RescueEventDto[];
}

/** Returned by status and grade changes so the client can offer Undo. */
export interface UndoToken {
  undoId: string;
}

export interface OverviewResponse {
  greetingName: string;
  needReply: number;
  top: RescueRowDto[];
  openCount: number;
  recoveredWeek: number;
  recoveredWeekCount: number;
  avgCallScore: number | null;
  /** 14 daily points, oldest first; null on days without scored calls. */
  scoreSeries: { day: string; score: number | null }[];
  /** `calls` is null for reps: they see teammates' results, not their call counts. */
  leaderboard: { rep: RepOption; score: number | null; calls: number | null }[];
  sources: { source: string; leads: number; booked: number }[];
  /** Graded leads (A–C) waiting on Revive. */
  revive: { count: number; value: number };
  currency: string;
  reps: RepOption[];
}

// ---------- Revive ----------

/** A good lead (graded A–C) that never booked and has gone quiet: nobody has called, texted or emailed either way since `quietSince`. */
export interface ReviveRowDto {
  leadId: string;
  leadName: string;
  grade: Grade;
  rep: RepOption | null;
  /** The AI's one-line reason for the grade. */
  reason: string | null;
  /** Must-haves not confirmed yet: what to ask about. Why a lead is B (one) or C (two or more). */
  missing: string[];
  /** Furthest journey milestone the lead reached. */
  milestone: string | null;
  /** Latest objection the AI tagged. */
  objection: string | null;
  /** The lead's last text or email, or their last call. */
  lastSaid: { channel: Channel; text: string | null; at: string } | null;
  value: number | null;
  quietSince: string;
  crmUrl: string;
}

export interface ReviveListResponse {
  rows: ReviveRowDto[];
  /** Leads sent from Revive to Rescue in the last 30 days, and how many of those recovered. */
  revived: { count: number; recovered: number; recoveredValue: number };
  /** A lead shows once it has been quiet this long. */
  quietDays: number;
  currency: string;
  reps: RepOption[];
}

// ---------- Team & settings ----------

export interface TeamMemberDto {
  id: string;
  name: string;
  email: string | null;
  crmRole: 'admin' | 'user';
  role: 'manager' | 'rep';
  active: boolean;
  /** Latest call, text or email this user sent. */
  lastActivityAt: string | null;
}

export interface TeamResponse {
  members: TeamMemberDto[];
  syncedAt: string | null;
  crm: 'highlevel';
}

export interface NotificationPrefs {
  highValueRescue: boolean;
  dailySummary: boolean;
  weeklyCoaching: boolean;
  unansweredLead: boolean;
}

export interface WorkspaceSettingsDto {
  name: string;
  timeZone: string;
  currency: string;
  replyTargetMin: number;
  backToOpenHours: number;
  defaultLeadValue: number;
  notifications: NotificationPrefs;
}

// ---------- Integrations ----------

export interface IntegrationsResponse {
  crm: 'highlevel';
  status: CompanyStatus;
  subAccount: string;
  locationMasked: string;
  lastSyncAt: string | null;
  importPct: number | null;
  synced: { calls: number; texts: number; emails: number; forms: number; contacts: number };
  formsAvailable: boolean;
  tags: number;
  calendars: number;
  milestones: number;
  aiOn: boolean;
}

// ---------- Leaderboard ----------

export type Period = 'week' | 'month' | 'quarter';

export interface LeaderboardRow {
  rep: RepOption;
  role: 'manager' | 'rep';
  score: number | null;
  /** Null for reps viewing: they see teammates' results, not their call counts. */
  calls: number | null;
  leads: number;
  bookedRate: number | null;
  speedToLeadMin: number | null;
  recovered: number;
  /** Score change against the previous period of the same length. */
  trend: number | null;
}

export interface LeaderboardResponse {
  period: Period;
  rows: LeaderboardRow[];
  currency: string;
}

// ---------- Journey setup ----------

export type MilestoneTrigger = 'tag' | 'appointment' | 'ai';
export type MilestoneRole = 'conversion' | 'goal' | null;

export interface MilestoneInput {
  /** Absent for a new milestone. */
  id?: string;
  name: string;
  trigger: MilestoneTrigger;
  /** Tag name, or calendar id for appointment triggers. */
  value: string;
  listenFor: string | null;
  optional: boolean;
  role: MilestoneRole;
}

export interface MilestoneDto extends MilestoneInput {
  id: string;
  /** Leads that reached it in the last 90 days. */
  leads: number;
}

export interface JourneySetupResponse {
  milestones: MilestoneDto[];
  tags: { name: string; leads: number }[];
  calendars: { id: string; name: string }[];
  templates: Record<string, MilestoneInput[]>;
  recoveryRule: 'next' | 'conversion' | 'goal';
  attributionDays: number;
}

export interface JourneySaveRequest {
  milestones: MilestoneInput[];
  recoveryRule: 'next' | 'conversion' | 'goal';
}

// ---------- Coaching ----------

export interface CoachingRepRow {
  rep: RepOption;
  role: 'manager' | 'rep';
  score: number | null;
  calls: number;
  /** Red and amber key moments in the period. */
  moments: number;
}

export interface CoachingListResponse {
  period: Period;
  reps: CoachingRepRow[];
}

export interface CoachingMoment {
  touchId: string;
  leadId: string;
  leadName: string;
  /** When the call happened. */
  at: string;
  /** Seconds into the recording. */
  t: number;
  text: string;
  flag: string;
  tone: 'red' | 'amber';
}

/** Team-wide objections from analyzed calls (Coaching → Objections). */
export interface ObjectionStatsResponse {
  period: Period;
  /** Analyzed calls in the period, and how many raised at least one objection. */
  calls: number;
  withObjection: number;
  /** Same buckets as the rep score chart: week 7 daily, month 4 weekly, quarter 13 weekly. Oldest first. */
  buckets: { label: string; calls: number }[];
  rows: {
    objection: string;
    heard: number;
    /** Percent of analyzed calls where it came up. */
    share: number;
    /** Share in the previous period of the same length; null when that period had no analyzed calls. */
    prevShare: number | null;
    handledPct: number;
    /** Percent of those calls that still ended with a next step booked. */
    bookedPct: number;
    /** Calls per bucket where it came up, aligned with `buckets`. */
    series: number[];
    /** Latest calls where it came up. */
    recent: { touchId: string; leadId: string; leadName: string; rep: string | null; at: string; booked: boolean }[];
  }[];
  /** Configured objections nobody raised in the period. */
  unheard: string[];
  /** True when an objection-handling criterion judges "handled well"; otherwise it would just repeat "booked after". */
  judged: boolean;
  handledRule: string;
  reps: RepOption[];
  timeZone: string;
}

export interface CoachingDetailResponse {
  period: Period;
  rep: RepOption;
  role: 'manager' | 'rep';
  score: number | null;
  trend: number | null;
  calls: number;
  bookedRate: number | null;
  speedToLeadMin: number | null;
  /** Week: 7 daily points. Month: 4 weekly. Quarter: 13 weekly. Oldest first. */
  series: { label: string; score: number | null }[];
  criteria: { name: string; passRate: number; calls: number }[];
  moments: CoachingMoment[];
  objections: { objection: string; heard: number; handledPct: number }[];
  /** How "handled well" is judged, shown under the table. */
  handledRule: string;
  timeZone: string;
}

/** An "AI hears it" milestone the AI heard on a lead's call, text or email. It counts as reached; a manager can remove it. */
export interface AiMilestoneDto {
  id: string;
  leadId: string;
  leadName: string;
  milestone: string;
  evidence: string;
  /** When it came up (the call, text or email), else when the AI heard it. */
  at: string;
  channel: Channel | null;
}

// ---------- Ask Lex ----------

export interface AskRequest {
  question: string;
  /** Earlier turns in this thread, oldest first (the answers' text only). */
  history: { question: string; answer: string }[];
}

export type AskBlock =
  | { kind: 'leads'; title: string; rows: { leadId: string; name: string; grade: Grade | null; rep: string | null; latest: string; meta: string; engagement: number; engagementLabel: EngagementLabel }[] }
  | { kind: 'chart'; title: string; unit: string; rows: { label: string; value: number; n: number }[] }
  | { kind: 'quotes'; title: string; rows: { touchId: string; leadId: string; leadName: string; channel: Channel; at: string; who: string; text: string }[] }
  | { kind: 'draft'; title: string; text: string; leadId: string | null; crmUrl: string | null };

export interface AskResponse {
  /** What Lex looked up, in order ("Searched leads: grade A or B, awaiting a reply"). */
  steps: string[];
  answer: string;
  blocks: AskBlock[];
  followup: string | null;
  /** "Live data from your CRM · synced 2 min ago" */
  basis: string;
  timeZone: string;
}
